/**
 * Confirmation dialog for `DELETE /api/admin/users/{id}` (204, or 409 for the
 * last active administrator).
 *
 * Deleting a user also drops their sessions server-side, so this is not
 * reversible from the UI — hence the explicit confirmation instead of a
 * one-click row action. The 409 detail ("Der letzte aktive Administrator kann
 * nicht deaktiviert, herabgestuft oder gelöscht werden.") is shown verbatim and
 * the dialog stays open so the administrator can back out.
 */

import { useState, type ReactNode } from 'react';

import { Banner } from '../../components/Banner';
import { Modal } from '../../components/Modal';
import Spinner from '../../components/Spinner';
import { deleteAdminUser, errorMessage } from '../../lib/api';
import { ROLE_LABELS } from '../../lib/types';
import type { AdminUser } from '../../lib/types';

export interface DeleteUserModalProps {
  user: AdminUser;
  /** True when this is the signed-in account — deleting it logs you out. */
  isSelf: boolean;
  onClose: () => void;
  /** Called after the 204 with a German success message. */
  onDeleted: (user: AdminUser, message: string) => void;
}

export function DeleteUserModal({
  user,
  isSelf,
  onClose,
  onDeleted,
}: DeleteUserModalProps): ReactNode {
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function onConfirm(): Promise<void> {
    setDeleting(true);
    setError(null);
    try {
      await deleteAdminUser(user.id);
      onDeleted(user, `Benutzer ${user.email} wurde gelöscht.`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Modal
      title="Benutzer löschen"
      subtitle={user.email}
      size="narrow"
      onClose={onClose}
      dismissable={!deleting}
      footer={
        <>
          <button type="button" className="danger" onClick={() => void onConfirm()} disabled={deleting}>
            {deleting ? <Spinner variant="on-accent" label="Wird gelöscht…" /> : 'Endgültig löschen'}
          </button>
          <button type="button" className="ghost" onClick={onClose} disabled={deleting}>
            Abbrechen
          </button>
        </>
      }
    >
      <div className="stack">
        <p style={{ margin: 0 }}>
          Das Konto <b>{user.email}</b>
          {user.name !== null && user.name !== '' ? ` (${user.name})` : ''} wird gelöscht. Aktive
          Sitzungen dieses Kontos werden dabei beendet. Der Vorgang kann nicht rückgängig gemacht
          werden.
        </p>
        {user.roles.length > 0 ? (
          <p className="muted" style={{ margin: 0, fontSize: 'var(--text-sm)' }}>
            Rollen:{' '}
            {user.roles
              .map((role) => {
                const labels: Readonly<Record<string, string | undefined>> = ROLE_LABELS;
                return labels[role] ?? role;
              })
              .join(', ')}
          </p>
        ) : null}
        {isSelf ? (
          <Banner kind="warn">
            Das ist Ihr eigenes Konto. Nach dem Löschen werden Sie abgemeldet.
          </Banner>
        ) : null}
        <Banner kind="warn">
          Alternative: das Konto im Bearbeiten-Dialog auf <b>inaktiv</b> setzen. Die Anmeldung ist
          dann gesperrt, die Historie bleibt erhalten.
        </Banner>
        {error !== null ? <Banner kind="error">{error}</Banner> : null}
      </div>
    </Modal>
  );
}

export default DeleteUserModal;
